import { Box, Grid } from '@mui/material'
import { memo } from 'react'
import { FINANCIAL_NAV_SECTIONS } from '../../util/financialData'
import { dualChartPanelSx, dualChartRowSx, VALUATION_PANEL_MIN_HEIGHT } from './financialChartLayout'

const TAB_BAR_HEIGHT = 34
const CHART_PANEL_MIN_HEIGHT = 336
const CHART_BODY_HEIGHT = 280

const TAB_LABEL_WIDTHS = [88, 72, 88, 104, 72, 96, 72, 120]

function FinancialDataSkeleton() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.1,
                    px: 2.1,
                    height: TAB_BAR_HEIGHT,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    overflow: 'hidden',
                }}
            >
                {FINANCIAL_NAV_SECTIONS.map((section, index) => (
                    <Box
                        key={section.id}
                        sx={{
                            width: TAB_LABEL_WIDTHS[index] ?? 96,
                            height: 16,
                            borderRadius: 0.5,
                            flexShrink: 0,
                        }}
                    />
                ))}
            </Box>

            <Box sx={{ flex: 1, overflow: 'hidden', px: 2, py: 2, minHeight: 0 }}>
                <Box sx={{ width: 72, height: 16, borderRadius: 0.5, mb: 1.5 }} />
                <Grid container spacing={2} sx={{ mb: 0 }}>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <Grid item xs={6} sm={4} key={`fact-${index}`}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ width: 72, height: 12, borderRadius: 0.5 }} />
                                <Box sx={{ width: index % 2 === 0 ? 88 : 64, height: 16, borderRadius: 0.5 }} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                    <Box sx={{ width: 48, height: 16, borderRadius: 0.5, mb: 1 }} />
                    <Box sx={{ width: '100%', maxWidth: 640, height: 14, borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: '92%', maxWidth: 600, height: 14, borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: '78%', maxWidth: 520, height: 14, borderRadius: 0.5 }} />
                </Box>

                <Box sx={{ mt: 3, width: '100%' }}>
                    <Box sx={{ width: 160, height: 16, borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: 240, height: 12, borderRadius: 0.5, mb: 1.5 }} />
                    <Box sx={dualChartRowSx}>
                        {[0, 1].map((panel) => (
                            <Box
                                key={`growth-${panel}`}
                                sx={{ ...dualChartPanelSx, minHeight: CHART_PANEL_MIN_HEIGHT }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ width: 88, height: 16, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 120, height: 28, borderRadius: 1 }} />
                                </Box>
                                <Box sx={{ height: CHART_BODY_HEIGHT, width: '100%', borderRadius: 1 }} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 2,
                                        mt: 0.75,
                                    }}
                                >
                                    <Box sx={{ width: 64, height: 14, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 72, height: 14, borderRadius: 0.5 }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ mt: 3, width: '100%' }}>
                    <Box sx={{ width: 72, height: 16, borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: 280, height: 12, borderRadius: 0.5, mb: 1.5 }} />
                    <Box sx={dualChartRowSx}>
                        {[0, 1].map((panel) => (
                            <Box
                                key={`valuation-${panel}`}
                                sx={{
                                    ...dualChartPanelSx,
                                    minHeight: VALUATION_PANEL_MIN_HEIGHT,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {panel === 0 ? (
                                    <>
                                        <Box sx={{ width: 100, height: 16, borderRadius: 0.5, mb: 1.5 }} />
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Box sx={{ width: 160, height: 160, borderRadius: '50%' }} />
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                            <Box sx={{ width: 72, height: 12, borderRadius: 0.5 }} />
                                            <Box sx={{ width: 72, height: 12, borderRadius: 0.5 }} />
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                            }}
                                        >
                                            <Box sx={{ width: 96, height: 16, borderRadius: 0.5 }} />
                                            <Box sx={{ width: 120, height: 28, borderRadius: 1 }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minHeight: 252, width: '100%', borderRadius: 1 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.75 }}>
                                            <Box sx={{ width: 36, height: 14, borderRadius: 0.5 }} />
                                            <Box sx={{ width: 36, height: 14, borderRadius: 0.5 }} />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ mt: 3, width: '100%' }}>
                    <Box sx={{ width: 88, height: 16, borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: 300, height: 12, borderRadius: 0.5, mb: 1.5 }} />
                    <Box sx={dualChartRowSx}>
                        {[0, 1].map((panel) => (
                            <Box
                                key={`ownership-${panel}`}
                                sx={{
                                    ...dualChartPanelSx,
                                    minHeight: VALUATION_PANEL_MIN_HEIGHT,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {panel === 0 ? (
                                    <>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                            }}
                                        >
                                            <Box sx={{ width: 96, height: 16, borderRadius: 0.5 }} />
                                            <Box sx={{ width: 100, height: 28, borderRadius: 1 }} />
                                        </Box>
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Box sx={{ width: 160, height: 160, borderRadius: '50%' }} />
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                            <Box sx={{ width: 56, height: 12, borderRadius: 0.5 }} />
                                            <Box sx={{ width: 40, height: 12, borderRadius: 0.5 }} />
                                            <Box sx={{ width: 40, height: 12, borderRadius: 0.5 }} />
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Box sx={{ width: 112, height: 16, borderRadius: 0.5, mb: 1 }} />
                                        <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                                            <Box sx={{ width: '52%', minHeight: 252, borderRadius: 1 }} />
                                            <Box sx={{ flex: 1, minWidth: 208, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {Array.from({ length: 4 }).map((_, index) => (
                                                    <Box
                                                        key={`ownership-legend-${index}`}
                                                        sx={{ width: '100%', height: 14, borderRadius: 0.5 }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ mt: 3, width: '100%' }}>
                    <Box sx={{ width: 104, height: 16, borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: 300, height: 12, borderRadius: 0.5, mb: 1.5 }} />
                    <Box sx={dualChartRowSx}>
                        {[0, 1].map((panel) => (
                            <Box
                                key={`health-${panel}`}
                                sx={{ ...dualChartPanelSx, minHeight: CHART_PANEL_MIN_HEIGHT }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ width: panel === 0 ? 148 : 156, height: 16, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 120, height: 28, borderRadius: 1 }} />
                                </Box>
                                <Box sx={{ height: CHART_BODY_HEIGHT, width: '100%', borderRadius: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.75 }}>
                                    <Box sx={{ width: 72, height: 14, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 80, height: 14, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 96, height: 14, borderRadius: 0.5 }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default memo(FinancialDataSkeleton)
